import { Fragment, useState } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser'; // processNodes // convertNodeToElement,
import api from 'utils/api';
import { formatDateTimeConversational } from 'utils/dateTime';
import { ConfirmModal } from 'shared/modals/confirmModal';
import localData from 'utils/localData';

import { UserContextMenu } from 'components/Menu';

import {
  Comment,
  UserAvatar,
  Content,
  Username,
  CreatedAt,
  HTMLParsedBody,
  DeleteLink,
} from './Styles';
import './styles.css';
import { getUserFullName } from 'utils/name';
import { USER_DATA } from 'shared/constants/users';
import { SUBMIT_TYPE } from '../BodyForm/constants';
import { CommentProptypes } from 'shared/propTypes/commentType';
import { DOLPHIN_API } from 'shared/constants/apis';
import logger from 'utils/logger';

const NODE_TYPE = {
  TAG: 'tag',
  SPAN: 'span',
  BLOCKQUOTE: 'blockquote',
};

const propTypes = {
  comment: PropTypes.shape(CommentProptypes).isRequired,
  fetchIssue: PropTypes.func.isRequired,
  issueAttachments: PropTypes.array.isRequired,
  handlePreviewButtonClick: PropTypes.func,
};

const log = logger.createLogger('ProjectBoardIssueDetailsComment');

const ProjectBoardIssueDetailsComment = ({
  comment,
  fetchIssue,
  issueAttachments,
  handlePreviewButtonClick,
}) => {
  const username = localData.get(USER_DATA.USERNAME);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const currentOrganizationId = localData.get(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );
  const commentUserFullName = getUserFullName(
    comment.user.firstName,
    comment.user.lastName
  );
  const commentUsername = comment.user.username;
  const isSelf = commentUsername === username;

  const handleCommentDelete = async () => {
    try {
      await api.delete(`${DOLPHIN_API.COMMENTS}/${comment.id}`, {
        username: username,
        organizationId: currentOrganizationId,
        params: {
          organizationId: currentOrganizationId,
        },
      });
      await fetchIssue();
    } catch (error) {
      console.error(error);
    }
  };

  //! This opens the preview or the viewer modal
  const onBlockquoteTextClick = (attachmentName, attachmentId) => {
    handlePreviewButtonClick(attachmentName, attachmentId);
  };

  const transform = (node) => {
    if (node.type === NODE_TYPE.TAG && node.name === NODE_TYPE.BLOCKQUOTE) {
      const attachmentId = node.attribs['data-id'];
      const attachment = issueAttachments.find(
        (attachment) => attachment.id === parseInt(attachmentId)
      );
      return (
        <div
          key={attachmentId ? attachmentId : new Date().getTime()}
          style={{ margin: '8px 0px 10px 0px' }}>
          <div
            className="file-link"
            onClick={() => {
              if (attachment) {
                onBlockquoteTextClick(node.children[0].data, attachmentId);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              backgroundColor: attachment ? '#f8f9fa' : '#f1f3f4',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: attachment ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              textDecoration: 'none',
              color: attachment ? '#495057' : '#9aa0a6',
              opacity: attachment ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (attachment) {
                e.target.style.backgroundColor = '#e9ecef';
                e.target.style.borderColor = '#adb5bd';
              }
            }}
            onMouseLeave={(e) => {
              if (attachment) {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#dee2e6';
              }
            }}>
            <span style={{ marginRight: '8px', fontSize: '16px' }}>
              {attachment
                ? attachment.structureJson.type === 'file'
                  ? '📄'
                  : '📁'
                : '❌'}
            </span>
            <span
              style={{
                fontWeight: '500',
                color: attachment ? '#007bff' : '#6c757d',
                textDecoration: attachment ? 'none' : 'strikethrough',
              }}>
              {node.children[0].data}
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                color: '#6c757d',
              }}>
              {attachment
                ? intl.get('issue_details_comment_attachment_highlight')
                : intl.get('issue_details_comment_attachment_deleted_message')}
            </span>
          </div>
        </div>
      );
    }
  };

  const ReactHtmlParserOptions = {
    decodeEntities: true,
    transform,
  };

  return (
    <Comment data-testid="issue-comment">
      <UserAvatar name={commentUserFullName} />
      <Content>
        {isSelf && (
          <Username>
            {commentUserFullName +
              ' ' +
              (comment.domainType === SUBMIT_TYPE.GLOBAL
                ? intl.get('issue_details_comment_label_published_globally')
                : intl.get('issue_details_comment_label_published_locally'))}
          </Username>
        )}
        {!isSelf && (
          <UserContextMenu
            username={commentUsername}
            userShowName={commentUserFullName}>
            <Username>
              {commentUserFullName +
                ' ' +
                (comment.domainType === SUBMIT_TYPE.GLOBAL
                  ? intl.get('issue_details_comment_label_published_globally')
                  : intl.get('issue_details_comment_label_published_locally'))}
            </Username>
          </UserContextMenu>
        )}
        <CreatedAt>{formatDateTimeConversational(comment.createdAt)}</CreatedAt>

        <Fragment>
          <div>
            <HTMLParsedBody>
              {ReactHtmlParser(comment.body, ReactHtmlParserOptions)}
            </HTMLParsedBody>
          </div>
          {username === comment.user.username && (
            <DeleteLink onClick={() => setIsDeleteModalOpen(true)}>
              {intl.get('issue_details_confirm_modal_button_delete_comment')}
            </DeleteLink>
          )}

          <ConfirmModal
            isOpened={isDeleteModalOpen}
            onConfirm={handleCommentDelete}
            title={intl.get(
              'issue_details_confirm_modal_text_delete_comment_title'
            )}
            bodyText={intl.get(
              'issue_details_confirm_modal_text_delete_comment_message'
            )}
            confirmButtonText={intl.get(
              'issue_details_confirm_modal_button_delete_comment_confirm_text'
            )}
            cancelButtonText={intl.get(
              'issue_details_confirm_modal_button_delete_comment_cancel_text'
            )}
            onCancel={() => setIsDeleteModalOpen(false)}
          />
        </Fragment>
      </Content>
    </Comment>
  );
};

ProjectBoardIssueDetailsComment.propTypes = propTypes;

export default ProjectBoardIssueDetailsComment;
